import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';

export default function DentalImplantsPage() {
  const { t } = useTranslation();

  const heroImage = 'https://readdy.ai/api/search-image?query=Professional%20dental%20implant%20cross-section%203D%20medical%20illustration%20showing%20titanium%20implant%20with%20ceramic%20crown%20in%20gum%20tissue%2C%20anatomical%20detail%20with%20bone%20structure%2C%20soft%20clinical%20lighting%2C%20clean%20white%20and%20beige%20background%2C%20high%20detail%20medical%20rendering%20style%2C%20no%20text%20no%20labels%20no%20logos&width=1200&height=800&seq=dental-implants-hero-01&orientation=landscape';

  const benefits = [
    {
      icon: 'ri-shield-check-line',
      title: t('implants.benefit1_title'),
      description: t('implants.benefit1_desc'),
    },
    {
      icon: 'ri-file-list-3-line',
      title: t('implants.benefit2_title'),
      description: t('implants.benefit2_desc'),
    },
    {
      icon: 'ri-sparkling-2-line',
      title: t('implants.benefit3_title'),
      description: t('implants.benefit3_desc'),
    },
  ];

  const steps = [
    {
      icon: 'ri-stethoscope-line',
      title: t('implants.path_step1_title'),
      description: t('implants.path_step1_desc'),
    },
    {
      icon: 'ri-clipboard-line',
      title: t('implants.path_step2_title'),
      description: t('implants.path_step2_desc'),
    },
    {
      icon: 'ri-surgical-mask-line',
      title: t('implants.path_step3_title'),
      description: t('implants.path_step3_desc'),
    },
    {
      icon: 'ri-heart-3-line',
      title: t('implants.path_step4_title'),
      description: t('implants.path_step4_desc'),
    },
  ];

  return (
    <>
      <Head
        title={t('seo.dental_implants.title')}
        metas={[
          { name: 'description', content: t('seo.dental_implants.description') },
          { name: 'keywords', content: t('seo.dental_implants.keywords') },
          { property: 'og:title', content: t('seo.dental_implants.title') },
          { property: 'og:description', content: t('seo.dental_implants.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/dental-implants' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { property: 'og:image', content: heroImage },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: t('seo.dental_implants.title') },
          { name: 'twitter:description', content: t('seo.dental_implants.description') },
          { name: 'twitter:image', content: heroImage },
          { name: 'last-modified', content: '2026-06-08' },
        ]}
        links={[{ rel: 'canonical', href: 'https://estevaclinic.com/dental-implants' }]}
      />
      <main className="bg-background-50 min-h-screen">
        <Navbar />

        {/* ── Hero ── */}
        <section className="pt-20 md:pt-32 pb-10 md:pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-16 items-center">
              <div className="flex-1 order-2 lg:order-1">
                <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-accent-600 font-label font-semibold">
                  {t('dental.tag')}
                </span>
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mt-3 md:mt-4">
                  {t('implants.hero_title')}
                </h1>
                <p className="mt-3 md:mt-5 text-sm text-foreground-500 leading-relaxed font-body">
                  {t('implants.hero_subtitle')}
                </p>
                <p className="mt-2 md:mt-3 text-sm text-foreground-500 leading-relaxed font-body">
                  {t('implants.hero_desc')}
                </p>
                <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/booking"
                    className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-primary-500 text-background-50 rounded-full text-xs tracking-[0.12em] uppercase font-label font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap no-underline"
                  >
                    {t('implants.cta_button')}
                    <i className="ri-arrow-right-line text-base"></i>
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 border border-foreground-200 text-foreground-700 rounded-full text-xs tracking-[0.12em] uppercase font-label font-semibold hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap no-underline"
                  >
                    {t('services.cta')}
                  </Link>
                </div>
              </div>
              <div className="flex-1 w-full order-1 lg:order-2">
                <img
                  src={heroImage}
                  alt={t('implants.hero_title')}
                  className="w-full rounded-2xl object-cover h-48 sm:h-64 md:h-80 lg:h-[420px]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Why Choose ── */}
        <section className="py-12 md:py-24 px-6 md:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="font-heading text-xl md:text-4xl font-bold text-foreground-900 leading-tight">
                {t('implants.why_title')}
              </h2>
              <p className="mt-3 md:mt-4 text-sm text-foreground-500 max-w-2xl mx-auto font-body leading-relaxed">
                {t('implants.why_intro')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {benefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className="flex flex-col items-center text-center p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/70 hover:border-accent-300/60 transition-colors"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className={`${benefit.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <h3 className="mt-4 font-heading text-sm md:text-base font-semibold text-foreground-900">
                    {benefit.title}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed font-body">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── When Indicated ── */}
        <section className="py-12 md:py-24 px-6 md:px-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-4xl font-bold text-foreground-900 leading-tight text-center">
              {t('implants.when_title')}
            </h2>
            <p className="mt-3 md:mt-4 text-sm text-foreground-500 leading-relaxed font-body text-center">
              {t('implants.when_desc')}
            </p>
            <p className="mt-2 text-sm text-foreground-500 leading-relaxed font-body text-center">
              {t('implants.when_desc2')}
            </p>
          </div>
        </section>

        {/* ── Process ── */}
        <section className="py-12 md:py-24 px-6 md:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-16">
              <h2 className="font-heading text-xl md:text-4xl font-bold text-foreground-900 leading-tight">
                {t('implants.path_title')}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
              {steps.map((step, idx) => (
                <div key={idx} className="relative group">
                  <div className="flex flex-row sm:flex-col items-center sm:text-center gap-4 sm:gap-0">
                    <div className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0 flex items-center justify-center rounded-2xl bg-primary-500 text-white group-hover:scale-110 transition-transform duration-300">
                      <i className={`${step.icon} text-lg md:text-2xl`}></i>
                    </div>
                    <div className="sm:mt-5">
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900">
                        {step.title}
                      </h3>
                      <p className="mt-1.5 md:mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed font-body">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)]">
                      <div className="border-t-2 border-dashed border-background-300"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-12 md:py-24 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <div className="rounded-3xl bg-primary-900 p-6 md:p-12 lg:p-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-800 via-primary-900 to-secondary-950/40"></div>
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-accent-500/5 blur-3xl"></div>
              <div className="relative z-10">
                <h2 className="font-heading text-xl md:text-4xl font-bold text-white leading-tight">
                  {t('implants.cta_title')}
                </h2>
                <p className="mt-3 text-sm text-white/60 max-w-lg mx-auto font-body">
                  {t('implants.cta_desc')}
                </p>
                <Link
                  to="/booking"
                  className="mt-6 md:mt-7 inline-flex items-center gap-2 px-6 md:px-10 py-3 md:py-4 bg-white text-primary-800 rounded-full text-xs md:text-sm tracking-[0.12em] uppercase font-label font-semibold hover:bg-white/95 hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap no-underline"
                >
                  {t('implants.cta_button')}
                  <i className="ri-arrow-right-line text-base"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}